import {
  DSButton,
  DSInput,
  DSToggle,
} from "@components/design-system/molecules";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { config, animated, useSpring } from "@react-spring/web";
import React, { useRef, useEffect, useState } from "react";

import { useFindAndReplace } from "../../../hooks";

export function FloatingWordMultiEditInput() {
  const {
    findAndReplaceMatchCount,
    findAndReplaceSelectedText,
    clearFindAndReplace,
    replacementText,
    setReplacementText,
    shouldShowReplacementTextBox,
    handleReplace,
    replacePartialWords,
    setReplacePartialWords,
  } = useFindAndReplace();

  const [isFocused, setIsFocused] = useState(false);

  const slideAnim = useSpring({
    transform: shouldShowReplacementTextBox
      ? "translateX(10px)"
      : "translateX(1000px)",
    config: config.default,
  });

  const inputRef = useRef(null);

  useEffect(() => {
    const handleHotkey = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "k") {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };

    const handleEnterKey = (event) => {
      if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        handleReplace();
      }
    };

    window.addEventListener("keydown", handleHotkey);
    window.addEventListener("keydown", handleEnterKey);

    return () => {
      window.removeEventListener("keydown", handleHotkey);
      window.removeEventListener("keydown", handleEnterKey);
    };
  }, [handleReplace]);

  // Disabled on super-soap due to virtualization causing issues with rendering this off screen
  // When we move off of a virtualized super-soap, this can be re-added
  const isSuperSOAP = location.pathname === "/super_soap";
  if (isSuperSOAP) {
    return null;
  }

  return (
    <animated.div
      style={slideAnim}
      className="fixed top-0 bottom-0 mt-24 mb-12 h-[calc(100vh-9rem)] right-10 z-20 flex pointer-events-none"
    >
      <div className="flex flex-col items-center pointer-events-auto">
        <button
          onClick={clearFindAndReplace}
          className="fixed top-1 right-4 z-20 text-xl text-danger"
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <div className="relative overflow-y-auto w-[320px] max-h-full flex flex-col items-center bg-primary py-4 px-10 rounded-standard space-y-2 border border-primary shadow-lg">
          <div className="flex flex-row items-center gap-sm">
            <h5 className="text-xs md:text-sm text-primary">
              Replace Partial Words
            </h5>
            <DSToggle
              checked={replacePartialWords}
              onChange={() =>
                setReplacePartialWords(!replacePartialWords)
              }
              dataTestId="replace-partial-words-toggle"
            />
          </div>
          <h5 className="text-xs pt-2 md:text-sm text-center text-primary">
            Replace {findAndReplaceMatchCount}{" "}
            {findAndReplaceMatchCount == 1 ? "instance" : "instances"}{" "}
            of{" "}
            <span className="italic">
              &quot;{findAndReplaceSelectedText}&quot;
            </span>{" "}
            with:
          </h5>

          <DSInput
            ref={inputRef}
            placeholder={
              isFocused ? "" : "Press Ctrl/Cmd + K to focus"
            }
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            defaultValue={findAndReplaceSelectedText}
            onChange={(e) => {
              const nextVal = e.target.value;
              setReplacementText(nextVal);
            }}
            value={replacementText}
            dataTestId="find-and-replace-input"
          />
          <div className="flex flex-row items-center">
            <DSButton
              disabled={!replacementText?.length}
              onClick={() => handleReplace()}
              dataTestId="find-and-replace-button"
            >
              Replace
            </DSButton>
          </div>
          <p className="text-xs text-secondary mt-2">
            Press Ctrl/Cmd + Enter to replace
          </p>
        </div>
      </div>
    </animated.div>
  );
}
